import UnitOrders from './UnitOrders';
import type { Perform } from './ResourceManager';
import MutualAidPanel from './MutualAidPanel';
import { available } from '../domain/availability';
import { VehicleEvidence } from './CatalogEvidence';
import { ArrowRight, BellRing, CarFront, Search, Radio } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type Incident, type SimState, statusLabels, isOpen, Service } from '../domain/model';
import { request } from '../api';
import { eta } from '../format';
import { vehicleDefinitions } from '../domain/organizations';
import { distanceKm } from '../providers/routing';
import { pointInArea } from '../domain/geography';
type Preview = {
  vehicleId: string;
  distanceKm?: number;
  totalSeconds?: number;
  travelSeconds?: number;
  turnoutSeconds?: number;
  error?: string;
  mode?: 'air' | 'road';
};
export default function FleetPanel({
  state,
  incident,
  selected,
  onSelected,
  enabled,
  onDispatch,
  onRequestStatus,
  onState,
  act,
}: {
  act: Perform;
  state: SimState;
  incident?: Incident;
  selected: string[];
  onSelected: (ids: string[]) => void;
  enabled: boolean;
  onDispatch: () => void;
  onState: (s: SimState) => void;
  onRequestStatus: (vehicleId: string) => void;
}) {
  const [filter, setFilter] = useState('Alle Kräfte'),
    [query, setQuery] = useState('');
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const previewKey = JSON.stringify([incident?.id, [...selected].sort()]);
  useEffect(() => {
    let cancelled = false;
    setPreviews({});
    if (!incident || !selected.length) return;
    const timer = setTimeout(() => {
      void request<{ previews: Preview[] }>('routing/preview', {
        incidentId: incident.id,
        vehicleIds: selected.slice(0, 30),
      })
        .then((result) => {
          if (!cancelled)
            setPreviews(Object.fromEntries(result.previews.map((p) => [p.vehicleId, p])));
        })
        .catch(() => {
          if (!cancelled)
            setPreviews(
              Object.fromEntries(
                selected.map((id) => [id, { vehicleId: id, error: 'Vorschau nicht verfügbar' }]),
              ),
            );
        });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [previewKey]);

  const belongsToArea = (stationId: string) => {
    const station = state.stations.find((candidate) => candidate.id === stationId);
    return Boolean(station && pointInArea(station.location, state.area, distanceKm));
  };
  const units = state.vehicles
    .filter(
      (v) =>
        (filter === 'Alle Kräfte' ||
          (filter === 'Mit Fahrzeugquelle' && !!v.evidence) ||
          (filter === 'Unbelegte Simulation' && !v.evidence) ||
          (filter === 'Verfügbar'
            ? available(v, state.now)
            : filter === 'Leitstellenbereich'
              ? belongsToArea(v.stationId)
              : filter === 'HvO / First Responder'
                ? v.kind === 'HvO'
                : filter === 'Spezialpolizei'
                  ? ['SEK', 'MEK', 'BePo', 'DHuFü', 'WaWe'].includes(v.kind)
                  : state.stations.find((w) => w.id === v.stationId)?.service === filter)) &&
        `${v.name} ${v.kind}`.toLowerCase().includes(query.toLowerCase()),
    )
    .sort((a, b) => {
      const areaOrder = Number(belongsToArea(b.stationId)) - Number(belongsToArea(a.stationId));
      if (areaOrder) return areaOrder;
      return incident
        ? distanceKm(a.location, incident.location) - distanceKm(b.location, incident.location)
        : a.name.localeCompare(b.name);
    });
  const dispatchable = incident && isOpen(incident) && incident.phase !== 'stabilisiert';
  return (
    <div className="fleet-panel">
      <div className="fleet-context">
        <div className="panel-title-icon">
          <CarFront size={22} />
        </div>
        <div>
          <h2>Fahrzeugdisposition</h2>
          <p>
            {incident
              ? `E ${incident.number} · ${incident.address}`
              : 'Einsatz auf der Karte auswählen'}
          </p>
        </div>
        <span className="count-badge">
          {state.vehicles.filter((v) => available(v, state.now)).length} verfügbar
        </span>
      </div>
      <MutualAidPanel
        state={state}
        incidentId={dispatchable ? incident?.id : undefined}
        enabled={enabled}
        onState={onState}
      />
      <p className="muted small fleet-evidence-note">
        Ausschließlich belegte Fahrzeuge aus deinem Leitstellengebiet.
        Nachbarkräfte kannst du über „Überlandhilfe anfordern“ für einen Einsatz ausleihen.
      </p>
      <div className="fleet-filters">
        <div className="search-field">
          <Search size={16} />
          <input
            aria-label="Fahrzeuge durchsuchen"
            placeholder="Funkrufname oder Fahrzeugtyp …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          aria-label="Fahrzeuge filtern"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {[
            'Alle Kräfte',
            'Verfügbar',
            'Leitstellenbereich',
            'Mit Fahrzeugquelle',
              'HvO / First Responder',
            'Spezialpolizei',
            ...Service.options,
          ].map((f) => (
            <option key={f}>{f}</option>
          ))}
        </select>
      </div>
      <div className="vehicle-table-wrap">
        <table className="vehicle-table">
          <thead>
            <tr>
              <th />
              <th>Funkrufname / Wache</th>
              <th>Typ</th>
              <th>Status</th>
              <th>Anfahrt</th>
              <th>Funk / Auftrag</th>
            </tr>
          </thead>
          <tbody>
            {units.map((v) => (
              <tr key={v.id} className={selected.includes(v.id) ? 'checked-row' : ''}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${v.name} auswählen`}
                    disabled={
                      !enabled || !available(v, state.now) || !dispatchable || !!v.catalogRetire
                    }
                    checked={selected.includes(v.id)}
                    onChange={(e) =>
                      onSelected(
                        e.target.checked
                          ? [...selected, v.id].slice(0, 30)
                          : selected.filter((id) => id !== v.id),
                      )
                    }
                  />
                </td>
                <td>
                  <span className="unit-name">
                    {v.name}
                    {v.incidentId === incident?.id && <i className="assigned-dot" />}
                    <em className={belongsToArea(v.stationId) ? 'area-unit' : 'mutual-aid-unit'}>
                      {belongsToArea(v.stationId) ? 'Gebiet' : 'Überörtlich'}
                    </em>
                  </span>
                  <small>{state.stations.find((w) => w.id === v.stationId)?.name}</small>
                  <VehicleEvidence vehicle={v} />
                </td>
                <td>
                  <span
                    className="unit-type"
                    title={`${vehicleDefinitions[v.kind].label}: ${vehicleDefinitions[v.kind].description}`}
                  >
                    {v.evidence?.typeLabel ?? v.kind}
                  </span>
                </td>
                <td>
                  <span className={`status-pill status-${v.status}`}>
                    <b>{v.status}</b>
                    {statusLabels[v.status]}
                  </span>
                  {state.speechRequests.some((r) => r.vehicleId === v.id) && (
                    <span className="speech-unit-label">S5 · Sprechwunsch</span>
                  )}
                </td>
                <td
                  className="mono"
                  title={
                    v.journey?.routeError ??
                    (previews[v.id]?.totalSeconds !== undefined
                      ? `${previews[v.id].distanceKm?.toFixed(1)} km · ${Math.ceil((previews[v.id].travelSeconds ?? 0) / 60)} Min. Fahrt + ${previews[v.id].turnoutSeconds} Sek. Ausrücken · ohne Live-Verkehr`
                      : 'Fahrzeug auswählen, um die Anfahrt berechnen zu lassen.')
                  }
                >
                  {available(v, state.now) && incident && selected.includes(v.id)
                    ? previews[v.id]?.totalSeconds !== undefined
                      ? `ca. ${Math.ceil(previews[v.id].totalSeconds! / 60)} min`
                      : previews[v.id]?.error
                        ? 'Route fehlt'
                        : 'Route lädt …'
                    : v.journey
                      ? `${v.journey.returning ? 'Rückfahrt ' : ''}${eta(v, state.now)}`
                      : available(v, state.now) && incident
                        ? 'bei Auswahl'
                        : '—'}
                </td>
                <td>
                  <button
                    className="radio-unit-button"
                    disabled={!enabled}
                    title="Status anfordern"
                    aria-label={`${v.name}: Status anfordern`}
                    onClick={() => onRequestStatus(v.id)}
                  >
                    <Radio size={16} />
                  </button>
                  <UnitOrders state={state} vehicle={v} act={act} enabled={enabled} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!units.length && <div className="empty">Keine passenden Fahrzeuge gefunden.</div>}
      </div>
      <div className="dispatch-footer">
        <div>
          <b>{selected.length}</b> Kräfte ausgewählt
          <small>Straßenroute + Ausrückzeit · ohne Live-Verkehr · Simulationszeit</small>
        </div>
        <button
          className="button primary"
          disabled={
            !enabled ||
            !selected.length ||
            !dispatchable ||
            state.vehicles.some((v) => selected.includes(v.id) && v.catalogRetire)
          }
          onClick={onDispatch}
        >
          <BellRing size={17} />
          Alarmieren <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
